import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";

/**
 * Shared MaterialReactTable wrapper for line items tables
 * Handles common functionality like grouping, column visibility, and toolbar
 */
export const SharedLineItemsTable = ({
  columns,
  data,
  onAddRow,
  availableGroupingKeys = ["Cost Code Tier 1", "Cost Code Tier 2", "Cost Type"],
  groupingKeyLabels,
}) => {
  const [grouping, setGrouping] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});

  const processedColumns = useMemo(() => columns, [columns]);

  const processedGroupingKeys = useMemo(
    () =>
      availableGroupingKeys.filter((k) => processedColumns.some((col) => col.accessorKey === k)),
    [availableGroupingKeys, processedColumns]
  );

  useEffect(() => {
    const vis = {};
    processedGroupingKeys.forEach((k) => {
      vis[k] = grouping.includes(k);
    });
    setColumnVisibility(vis);
  }, [grouping, processedGroupingKeys]);

  return (
    <Box sx={{ width: "100%", overflow: "visible" }}>
      <MaterialReactTable
        columns={processedColumns}
        data={data}
        enableStickyHeader
        enableColumnResizing
        enableSorting={false}
        enablePagination={false}
        enableColumnOrdering={false}
        enableColumnDragging={false}
        enableGrouping
        enableExpanding
        enableColumnActions={false}
        positionToolbarDropZone="none"
        enableColumnFilters={false}
        enableFacetedValues={false}
        enableGlobalFilter={false}
        layoutMode="grid-auto"
        autoResetAll={false}
        autoResetExpanded={false}
        autoResetGrouping={false}
        initialState={{
          density: "compact",
          expanded: true,
        }}
        state={{ grouping, columnVisibility }}
        onGroupingChange={setGrouping}
        displayColumnDefOptions={{
          "mrt-row-expand": {
            size: 0,
            enableResizing: false,
          },
        }}
        muiTopToolbarProps={{
          sx: {
            p: 0,
            pb: 1,
          },
        }}
        muiBottomToolbarProps={{
          sx: { display: "none" },
        }}
        renderTopToolbarCustomActions={() => (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", px: 1, width: "100%" }}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel id="grouping-select-label">Grouping</InputLabel>
              <Select
                multiple
                labelId="grouping-select-label"
                label="Grouping"
                value={grouping}
                onChange={(e) => setGrouping(e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((val) => {
                      const label = groupingKeyLabels?.[val] || val;
                      return <Chip key={val} label={label} size="small" />;
                    })}
                  </Box>
                )}
              >
                {processedGroupingKeys.map((k) => {
                  const label = groupingKeyLabels?.[k] || k;
                  return (
                    <MenuItem key={k} value={k}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            <Button variant="outlined" size="small" onClick={onAddRow}>
              Add Line Item
            </Button>
          </Box>
        )}
      />
    </Box>
  );
};

SharedLineItemsTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  onAddRow: PropTypes.func.isRequired,
  availableGroupingKeys: PropTypes.arrayOf(PropTypes.string),
  groupingKeyLabels: PropTypes.objectOf(PropTypes.string),
};

/**
 * Helper function to create hidden grouping columns
 */
export const createHiddenGroupingColumns = () => [
  { accessorKey: "Cost Code Tier 1", header: "Cost Code Tier 1", enableGrouping: true },
  { accessorKey: "Cost Code Tier 2", header: "Cost Code Tier 2", enableGrouping: true },
  { accessorKey: "Cost Type", header: "Cost Type", enableGrouping: true },
];
